/**
 * Base Tier Pricing Domain Module
 *
 * Per-region seat-based base-tier pricing, stored as a JSONB array on
 * `region_pricing.base_tier_pricing`. Part of the billing bounded context.
 *
 * See `docs/specs/billing/2026-04-17-001-consolidate-pricing-tiers-into-region-pricing.md`
 * §3.2, §5.1, §10 for the invariant set and schema contract.
 *
 * Pure domain: no @effect/sql, no repository dependencies.
 */
import { ParseResult, Schema as S } from "effect"

// =============================================================================
// Primitive: tierKey
// =============================================================================

export const TierKey = S.String.pipe(
  S.pattern(/^[a-z][a-z0-9_]*$/, {
    message: () => "tierKey must be snake_case starting with a lowercase letter"
  })
)
export type TierKey = S.Schema.Type<typeof TierKey>

// =============================================================================
// Single base-tier entry
// =============================================================================

export const BaseTierEntry = S.Struct({
  tierKey: TierKey,
  tierName: S.String.pipe(S.minLength(1)),
  minSeats: S.Int.pipe(S.greaterThanOrEqualTo(1)),
  maxSeats: S.NullOr(S.Int.pipe(S.greaterThanOrEqualTo(1))),
  costPerSeatCents: S.Int.pipe(S.greaterThanOrEqualTo(0))
})
export type BaseTierEntry = S.Schema.Type<typeof BaseTierEntry>

// =============================================================================
// Refined array: full BaseTierPricing invariant set (§3.2)
// =============================================================================

export const BaseTierPricing = S.Array(BaseTierEntry).pipe(
  S.minItems(1),
  S.filter((entries, _options, ast) => {
    const sorted = [...entries].sort((a, b) => a.minSeats - b.minSeats)

    const keys = new Set(sorted.map((e) => e.tierKey))
    if (keys.size !== sorted.length) {
      return new ParseResult.Type(ast, entries, "Duplicate tierKey")
    }

    if (sorted[0].minSeats !== 1) {
      return new ParseResult.Type(
        ast,
        entries,
        "First tier must start at minSeats = 1"
      )
    }

    const unbounded = sorted.filter((e) => e.maxSeats === null)
    if (unbounded.length !== 1 || unbounded[0] !== sorted[sorted.length - 1]) {
      return new ParseResult.Type(
        ast,
        entries,
        "Exactly one tier must have maxSeats = null, and it must be the last tier"
      )
    }

    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i]
      const b = sorted[i + 1]
      if (a.maxSeats === null) {
        return new ParseResult.Type(ast, entries, "Unbounded tier must be last")
      }
      if (a.maxSeats < a.minSeats) {
        return new ParseResult.Type(
          ast,
          entries,
          `Tier "${a.tierKey}" has maxSeats < minSeats`
        )
      }
      if (b.minSeats !== a.maxSeats + 1) {
        return new ParseResult.Type(
          ast,
          entries,
          `Gap or overlap between "${a.tierKey}" and "${b.tierKey}"`
        )
      }
    }

    return undefined
  })
)
export type BaseTierPricing = S.Schema.Type<typeof BaseTierPricing>

// =============================================================================
// Override entry / array
// =============================================================================
// Overrides carry tierKey for identity. tierName / minSeats / maxSeats are
// present for shape parity with the region row but are ignored at merge time
// (§4.2) — display and ranges always flow from the region row.

export const BaseTierOverrideEntry = S.Struct({
  tierKey: TierKey,
  tierName: S.optional(S.String),
  minSeats: S.optional(S.Int),
  maxSeats: S.optional(S.NullOr(S.Int)),
  costPerSeatCents: S.Int.pipe(S.greaterThanOrEqualTo(0))
})
export type BaseTierOverrideEntry = S.Schema.Type<typeof BaseTierOverrideEntry>

export const BaseTierOverride = S.Array(BaseTierOverrideEntry)
export type BaseTierOverride = S.Schema.Type<typeof BaseTierOverride>

// =============================================================================
// Pure functions
// =============================================================================

/**
 * Finds the base-tier entry matching a given seat count. Returns the first
 * entry where `seatCount >= minSeats` and (`maxSeats === null` OR `seatCount
 * <= maxSeats`). Every entry in the array is considered active — there is no
 * `isActive` field on `BaseTierEntry`.
 */
export const matchTier = (
  seatCount: number,
  tiers: ReadonlyArray<BaseTierEntry>
): BaseTierEntry | undefined =>
  tiers.find(
    (tier) =>
      seatCount >= tier.minSeats &&
      (tier.maxSeats === null || seatCount <= tier.maxSeats)
  )

/**
 * Returns the unique list of `tierKey` values present in `override` that are
 * NOT present in `regionTiers`. An empty array means every override entry
 * matches a region tier (valid cascade).
 */
export const findUnmatchedOverrideKeys = (
  override: ReadonlyArray<BaseTierOverrideEntry>,
  regionTiers: ReadonlyArray<BaseTierEntry>
): ReadonlyArray<string> => {
  const regionKeys = new Set(regionTiers.map((t) => t.tierKey))
  const unmatched = new Set<string>()
  for (const entry of override) {
    if (!regionKeys.has(entry.tierKey)) {
      unmatched.add(entry.tierKey)
    }
  }
  return Array.from(unmatched)
}
